import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Pencil,
  ArrowLeft,
  CreditCard,
  AlertCircle,
} from 'lucide-react';
import { paymentService } from '../../services/paymentService.js';
import { shipmentService } from '../../services/shipmentService.js';
import Alert from '../common/Alert.js';
import {
  PageLayout,
  Button,
  FormField,
  DetailCard,
} from '../common/index.js';

export default function EditPayment() {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [originalPayment, setOriginalPayment] = useState(null);
  const [formData, setFormData] = useState({
    payment_amount: '',
    payment_mode: 'cash',
    remarks: '',
  });

  const paymentModes = [
    { value: 'cash', label: 'CASH' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
  ];

  useEffect(() => {
    fetchPaymentData();
  }, [paymentId]);

  const fetchPaymentData = async () => {
    try {
      setFetchingData(true);
      const response = await paymentService.getPaymentById(paymentId);
      const payment = response.data.data;
      setOriginalPayment(payment);
      setFormData({
        payment_amount: payment.payment_amount || '',
        payment_mode: payment.payment_mode || 'cash',
        remarks: payment.remarks || '',
      });

      const shipmentRes = await shipmentService.getShipment(payment.shipment_id);
      setShipmentDetails(shipmentRes.data.shipment || shipmentRes.data.data);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch payment data');
      navigate('/payment-history');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'payment_amount') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const calculateNewBalance = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const maxAllowed = shipmentDetails ? shipmentDetails.balance_amount + originalPayment.payment_amount : 0;
    return parseFloat((maxAllowed - paymentAmount).toFixed(2));
  };

  const isPaymentAmountExceeded = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const maxAllowed = shipmentDetails ? shipmentDetails.balance_amount + originalPayment.payment_amount : 0;
    return parseFloat(paymentAmount.toFixed(2)) > parseFloat(maxAllowed.toFixed(2));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentAmount = parseFloat(formData.payment_amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      Alert.error('Please enter a valid payment amount');
      return;
    }

    const maxAllowed = shipmentDetails.balance_amount + originalPayment.payment_amount;
    if (parseFloat(paymentAmount.toFixed(2)) > parseFloat(maxAllowed.toFixed(2))) {
      Alert.error(`Payment amount cannot exceed ₹${maxAllowed.toFixed(2)}`);
      return;
    }

    const confirmed = await Alert.confirm(
      `Update payment amount to ₹${paymentAmount.toFixed(2)}?`,
      'Confirm Update'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await paymentService.updatePayment(paymentId, formData);
      Alert.success('Payment updated successfully');
      navigate('/payment-history');
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <PageLayout title="Edit Payment" icon={Pencil}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading payment data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const maxAllowed = shipmentDetails ? shipmentDetails.balance_amount + originalPayment.payment_amount : 0;
  const newBalance = calculateNewBalance();
  const isAmountExceeded = isPaymentAmountExceeded();
  const isSubmitDisabled = loading || isAmountExceeded;

  return (
    <PageLayout
      title="Edit Payment"
      icon={Pencil}
      maxWidth="max-w-7xl"
      actions={
        <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/payment-history')}>
          Back to Payment History
        </Button>
      }
    >
      {shipmentDetails && originalPayment && (
        <DetailCard title="Payment Summary" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Shipment ID</p>
              <p className="font-bold text-brand font-mono text-lg">#{originalPayment.shipment_id}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Current Balance</p>
              <p className="font-bold text-red-700 text-lg">₹{shipmentDetails.balance_amount?.toFixed(2)}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Original Payment</p>
              <p className="font-bold text-secondary text-lg">₹{originalPayment.payment_amount?.toFixed(2)}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-brand uppercase tracking-wide mb-1">Maximum Allowed</p>
              <p className="font-bold text-brand text-lg">₹{maxAllowed.toFixed(2)}</p>
            </div>
          </div>
        </DetailCard>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-card shadow-card p-4 sm:p-6 lg:p-8"
      >
        <h3 className="text-lg font-semibold text-secondary mb-4 pb-3 border-b border-gray-100">
          Update Payment Details
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <FormField label="Payment Amount" required>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500 font-semibold">₹</span>
              </div>
              <input
                type="text"
                name="payment_amount"
                value={formData.payment_amount}
                onChange={handleChange}
                placeholder="0.00"
                required
                className={`input-clean pl-9 font-semibold ${
                  isAmountExceeded && formData.payment_amount ? 'border-danger focus:ring-danger/30' : ''
                }`}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs">
              <span className="text-gray-500">
                Maximum: <span className="font-semibold text-gray-700">₹{maxAllowed.toFixed(2)}</span>
              </span>
              {formData.payment_amount && (
                <>
                  {isAmountExceeded ? (
                    <span className="text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Amount Exceeds Maximum
                    </span>
                  ) : (
                    <span className="text-brand font-semibold bg-brand-light px-3 py-1 rounded-full">
                      New Balance: ₹{newBalance.toFixed(2)}
                    </span>
                  )}
                </>
              )}
            </div>
          </FormField>

          <FormField label="Payment Mode" required>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <CreditCard className="w-5 h-5 text-gray-400" />
              </div>
              <select
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleChange}
                required
                className="input-clean pl-12 font-semibold"
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
          </FormField>

          <FormField label="Remarks (Optional)" className="md:col-span-2">
            <div className="relative">
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Add any additional notes or comments about this payment..."
                rows={4}
                className="input-clean resize-none"
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                {formData.remarks.length} characters
              </div>
            </div>
          </FormField>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-6 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={() => navigate('/payment-history')}
            className="sm:w-auto w-full"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            loading={loading}
            className="sm:w-auto w-full sm:ml-auto"
          >
            {loading ? 'Updating Payment...' : 'Update Payment'}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
}
